import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { sendCheckoutOtp, verifyCheckoutOtp } from '../../fraud';

/**
 * OtpChallenge
 *
 * The "Challenge" step of the fraud flow (Stage 3/4). Shown when the
 * pre-payment engine returns decision `challenge` with challenge_type `otp`.
 * Collects the buyer's phone, sends a one-time code over SMS, and verifies it.
 * On success it calls onVerified() and the caller resumes the payment; the
 * charge is never attempted until the code is verified.
 *
 * Props:
 *   open        — whether the modal is visible
 *   defaultPhone— prefill (e.g. the logged-in user's phone)
 *   onVerified  — called once the OTP is verified
 *   onCancel    — called when the buyer dismisses the challenge
 */
export default function OtpChallenge({ open, defaultPhone = '', onVerified, onCancel }) {
    const [phone, setPhone] = useState(defaultPhone || '');
    const [code, setCode] = useState('');
    const [stage, setStage] = useState('phone'); // 'phone' | 'code'
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (open) {
            setPhone(defaultPhone || '');
            setCode('');
            setStage('phone');
            setError('');
            setInfo('');
            setCooldown(0);
        }
    }, [open, defaultPhone]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    if (!open) return null;

    const handleSend = async () => {
        setError('');
        setInfo('');
        if (!phone || phone.replace(/\D/g, '').length < 7) {
            setError('Please enter a valid phone number.');
            return;
        }
        setBusy(true);
        const res = await sendCheckoutOtp(phone);
        setBusy(false);

        if (res.status === 'success' || res.state === 'sent') {
            setStage('code');
            setInfo('We sent a verification code to your phone.');
            setCooldown(Number(res.cooldown) || 45);
        } else if (res.state === 'cooldown') {
            setStage('code');
            setInfo(res.message || 'Please wait before requesting another code.');
            setCooldown(Number(res.cooldown) || 45);
        } else {
            setError(res.message || 'Could not send the verification code.');
        }
    };

    const handleVerify = async () => {
        setError('');
        if (!code || code.replace(/\D/g, '').length < 4) {
            setError('Enter the code from your SMS.');
            return;
        }
        setBusy(true);
        const res = await verifyCheckoutOtp(code);
        setBusy(false);

        if (res.verified) {
            // Hand back the number that was actually verified. The buyer can
            // change it here, and it used to die with this modal — the SMS went
            // to the new number while the order kept the old one.
            onVerified?.(phone);
        } else {
            setError(res.message || 'That code is not correct.');
        }
    };

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Verify your phone to continue"
        >
            <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                <button
                    type="button"
                    onClick={onCancel}
                    className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                    aria-label="Cancel verification"
                >
                    <X size={20} />
                </button>

                <h3 className="mb-1 text-lg font-semibold text-gray-900">Quick security check</h3>
                <p className="mb-4 text-sm text-gray-500">
                    For your protection we need to verify your phone before completing this payment.
                </p>

                {stage === 'phone' ? (
                    <>
                        <label className="mb-1 block text-xs font-medium text-gray-600">Phone number</label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+1 876 555 1234"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400"
                        />
                        <button
                            type="button"
                            disabled={busy}
                            onClick={handleSend}
                            className="mt-4 w-full rounded-xl bg-black py-3 text-sm font-semibold text-white disabled:opacity-60"
                        >
                            {busy ? 'Sending…' : 'Send code'}
                        </button>
                    </>
                ) : (
                    <>
                        <label className="mb-1 block text-xs font-medium text-gray-600">Verification code</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="Enter code"
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-center text-lg tracking-widest outline-none focus:border-gray-400"
                        />
                        <button
                            type="button"
                            disabled={busy}
                            onClick={handleVerify}
                            className="mt-4 w-full rounded-xl bg-black py-3 text-sm font-semibold text-white disabled:opacity-60"
                        >
                            {busy ? 'Verifying…' : 'Verify & continue'}
                        </button>
                        <button
                            type="button"
                            disabled={busy || cooldown > 0}
                            onClick={handleSend}
                            className="mt-2 w-full text-center text-xs font-medium text-gray-500 disabled:opacity-50"
                        >
                            {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                        </button>
                    </>
                )}

                {info ? <p className="mt-3 text-xs text-green-600">{info}</p> : null}
                {error ? <p className="mt-3 text-xs text-red-600">{error}</p> : null}
            </div>
        </div>
    );
}
